<template>
  <div class="ui-panel">
    <div class="ui-panel__head">
      <ui-avatar :text="initialsOf(name)" tone="accent" big />
      <div>
        <small>#{{ restaurantId }}</small>
      </div>
    </div>

    <ui-state v-if="status === 'loading'" kind="loading" :tiles="0" :rows="3" />
    <ui-banner v-else-if="status === 'error'" tone="bad" role="alert" lg icon="mdi-alert-outline">
      {{ $t("contracts_shell.panel.error") }}
      <button type="button" class="ui-link" @click="load">{{ $t("contracts_shell.state.retry") }}</button>
    </ui-banner>

    <template v-else>
      <ui-banner v-if="!canEdit" tone="info">{{ $t("services_read_only_notice") }}</ui-banner>

      <div class="ui-actions">
        <ui-button v-if="canEdit" variant="primary" icon="mdi-file-document-plus-outline" @click="shell.openNewContract(restaurant)">
          {{ contracts.length ? $t("renew") : $t("new_contract") }}
        </ui-button>
        <ui-button icon="mdi-handshake-outline" @click="shell.openServices(restaurant)">{{ $t("services_and_terms") }}</ui-button>
      </div>

      <ui-section :title="`${$t('contracts_title')} · ${contracts.length}`">
        <ui-state v-if="!contracts.length" kind="empty" tight icon="mdi-file-document-outline" :title="$t('contracts_shell.panel.empty_title')" :text="$t('contracts_shell.panel.empty_text')" />
        <ul v-else class="cs-contracts">
          <li v-for="(contract, index) in contracts" :key="contract.id" class="cs-contract" :class="`cs-contract--${roleOf(contract, index)}`">
            <div class="cs-contract__top">
              <b>{{ contract.contract_number }}</b>
              <ui-chip :tone="roleTone(roleOf(contract, index))" dot>{{ roleText(contract, index) }}</ui-chip>
              <span class="cs-contract__gap"></span>
              <a v-if="documentHref(contract)" class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain" :href="documentHref(contract)" target="_blank" rel="noopener noreferrer" :title="$t('contracts_shell.panel.document')" :aria-label="$t('contracts_shell.panel.document')">
                <v-icon size="18">mdi-paperclip</v-icon>
              </a>
              <ui-icon-button v-if="canEdit" icon="mdi-pencil-outline" size="sm" plain :label="`${$t('contracts_shell.panel.edit')}: ${contract.contract_number}`" :title="$t('contracts_shell.panel.edit')" @click="shell.openEditContract(restaurant, contract)" />
            </div>
            <div class="cs-contract__meta ui-num">{{ periodText(contract.valid_from, contract.valid_to) }}</div>
            <div class="cs-contract__meta">
              <span>{{ $t("contract_date") }}: <span class="ui-num">{{ localeDate(contract.contract_date) }}</span></span>
              <span>{{ $t("contract_items", { count: contract.broj_usluga }) }}</span>
              <span>{{ $t("created_by") }}: {{ contract.created_by ? toLatin(contract.created_by) : "—" }}</span>
            </div>
            <div v-if="roleOf(contract, index) === 'expired-current'" class="cs-billing">{{ $t("still_being_billed") }}</div>
            <div v-if="contract.note" class="cs-contract__note">{{ contract.note }}</div>
          </li>
        </ul>
      </ui-section>
    </template>
  </div>
</template>

<script>
import contractsService from "@/services/contracts";
import { contractStatusLabel } from "@/utils/contractStatus";
import hasContractLabels from "../hasContractLabels";
import { contractRole } from "../contractsLogic";

const ROLE_TONES = { valid: "ok", expiring: "warn", "expired-current": "bad", replaced: "plain", other: "plain" };

/**
 * Panel restorana: istorija ugovora (najnoviji prvi) i radnje. Novi ugovor / obnova, izmjena ugovora i usluge sa cijenama otvaraju se
 * u postojećim dijalozima koje hostuje ljuska; kad se oni sačuvaju, ljuska podigne `version` pa se istorija ponovo učita.
 * Ugovor koji je zatvorio noviji dolazi sa backenda kao ISTEKAO, ali se ovdje piše "Zamijenjen novijim ugovorom" (bez alarma).
 */
export default {
  name: "RestaurantPanel",
  mixins: [hasContractLabels],
  inject: ["reportShell"],
  props: {
    restaurantId: { type: Number, required: true },
    name: { type: String, default: "" },
  },
  data: () => ({ status: "loading", contracts: [], canEdit: false }),
  computed: {
    shell() {
      return this.reportShell;
    },
    restaurant() {
      return { id: this.restaurantId, name: this.name };
    },
  },
  watch: {
    restaurantId() {
      this.load();
    },
    "shell.version"() {
      this.load(true);
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load(silent = false) {
      if (!silent) this.status = "loading";
      try {
        const { data } = await contractsService.getRestaurantContracts(this.restaurantId);
        this.contracts = (data && data.contracts) || [];
        this.canEdit = Boolean(data) && data.can_edit !== false;
        this.status = "ready";
      } catch (error) {
        if (!silent) this.status = "error";
      }
    },
    roleOf(contract, index) {
      return contractRole(contract, index);
    },
    roleTone(role) {
      return ROLE_TONES[role] || "plain";
    },
    roleText(contract, index) {
      if (this.roleOf(contract, index) === "replaced") return this.$t("contracts_shell.panel.replaced");
      return this.toLatin(contractStatusLabel(contract, this.$t.bind(this)));
    },
    /** veza na dokument iz podataka: samo http(s) i putanje sa servera, ne `javascript:` */
    documentHref(contract) {
      const url = contract.document_url;
      return typeof url === "string" && /^(https?:\/\/|\/)/i.test(url.trim()) ? url.trim() : "";
    },
  },
};
</script>
